import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageToggle from '../../components/common/LanguageToggle';
import {
  ArrowRight, Clock, Shield, BarChart3,
  Sun, Moon, Globe, Smartphone, RefreshCw, Users,
} from 'lucide-react';
import OperisLogo from '../../components/common/OperisLogo';
import AnimatedClock from '../../components/common/AnimatedClock';
import TimeRain from '../../components/landing/TimeRain';
import WaveRiver from '../../components/landing/WaveRiver';
import { useTheme } from '../../context/ThemeContext';

/* ─── Scroll-accumulating rings ─── */
const scrollRings = [
  { diameter: 410, threshold: 0, color: '#d97706', colorLight: '#d97706', opacity: 0.22, opacityLight: 0.18, dashed: true, spin: 30 },
  { diameter: 460, threshold: 0.08, color: '#f59e0b', colorLight: '#f59e0b', opacity: 0.18, opacityLight: 0.12, dashed: false, spin: -45 },
  { diameter: 510, threshold: 0.20, color: '#ea580c', colorLight: '#b45309', opacity: 0.14, opacityLight: 0.08, dashed: true, spin: 55 },
  { diameter: 560, threshold: 0.35, color: '#d97706', colorLight: '#d97706', opacity: 0.10, opacityLight: 0.06, dashed: true, spin: -38 },
  { diameter: 610, threshold: 0.52, color: '#f59e0b', colorLight: '#f59e0b', opacity: 0.06, opacityLight: 0.04, dashed: false, spin: 50 },
];

const LandingPage = () => {
  const { darkMode, toggleTheme } = useTheme();
  const { t } = useTranslation();

  const features = useMemo(
    () => [
      {
        icon: Clock,
        title: t('landing.features.smartTime.title'),
        desc: t('landing.features.smartTime.desc'),
        gradient: 'from-blue-500 to-indigo-600',
      },
      {
        icon: BarChart3,
        title: t('landing.features.sapSync.title'),
        desc: t('landing.features.sapSync.desc'),
        gradient: 'from-indigo-500 to-purple-600',
      },
      {
        icon: Shield,
        title: t('landing.features.secureAccess.title'),
        desc: t('landing.features.secureAccess.desc'),
        gradient: 'from-purple-500 to-pink-500',
      },
    ],
    [t]
  );

  const capabilities = useMemo(
    () => [
      { icon: Globe, title: t('landing.capabilities.cloudFirst.title'), desc: t('landing.capabilities.cloudFirst.desc') },
      { icon: Smartphone, title: t('landing.capabilities.mobileReady.title'), desc: t('landing.capabilities.mobileReady.desc') },
      { icon: RefreshCw, title: t('landing.capabilities.realtimeSync.title'), desc: t('landing.capabilities.realtimeSync.desc') },
      { icon: Users, title: t('landing.capabilities.multiTenant.title'), desc: t('landing.capabilities.multiTenant.desc') },
    ],
    [t]
  );

  const trustPillars = useMemo(
    () => [
      {
        Icon: RefreshCw,
        title: t('landing.trust.sapWorkflows.title'),
        desc: t('landing.trust.sapWorkflows.desc'),
      },
      {
        Icon: Shield,
        title: t('landing.trust.controlledAccess.title'),
        desc: t('landing.trust.controlledAccess.desc'),
      },
      {
        Icon: Smartphone,
        title: t('landing.trust.webField.title'),
        desc: t('landing.trust.webField.desc'),
      },
      {
        Icon: BarChart3,
        title: t('landing.trust.operationalClarity.title'),
        desc: t('landing.trust.operationalClarity.desc'),
      },
    ],
    [t]
  );

  /* ── Scroll progress ── */
  const [scrollProgress, setScrollProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const heroH = window.innerHeight * 1.2;
      setScrollProgress(Math.min(1, window.scrollY / heroH));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* ── Staggered fade-in ── */
  const [visible, setVisible] = useState({});
  useEffect(() => {
    const stages = [
      ['logo', 150], ['badge', 400], ['heading', 650],
      ['subtext', 900], ['ctas', 1150], ['pillars', 1400], ['clock', 700],
    ];
    const timers = stages.map(([key, ms]) =>
      setTimeout(() => setVisible(v => ({ ...v, [key]: true })), ms)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  const fade = (key) =>
    `transition-all duration-700 ease-out ${visible[key] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`;

  /* ── Section observers ── */
  const featuresRef = useRef(null);
  const capsRef = useRef(null);
  const [featuresVisible, setFeaturesVisible] = useState(false);
  const [capsVisible, setCapsVisible] = useState(false);

  useEffect(() => {
    const observe = (ref, setter) => {
      const el = ref.current;
      if (!el) return () => {};
      const obs = new IntersectionObserver(
        ([e]) => { if (e.isIntersecting) { setter(true); obs.disconnect(); } },
        { threshold: 0.15 }
      );
      obs.observe(el);
      return () => obs.disconnect();
    };
    const c1 = observe(featuresRef, setFeaturesVisible);
    const c2 = observe(capsRef, setCapsVisible);
    return () => { c1(); c2(); };
  }, []);

  /* ── Theme-dependent classes ── */
  const bg = darkMode ? 'bg-[#0f0f14]' : 'bg-gray-50';
  const gradientOverlay = darkMode
    ? 'bg-gradient-to-br from-[#0f0f14] via-[#1a1510] to-[#251c10]/70'
    : 'bg-gradient-to-br from-gray-50 via-white to-amber-50/50';
  const headingColor = darkMode ? 'text-white' : 'text-gray-900';
  const bodyColor = darkMode ? 'text-white/60' : 'text-gray-500';
  const mutedColor = darkMode ? 'text-white/50' : 'text-gray-400';
  const cardBg = darkMode
    ? 'border-amber-500/10 bg-white/[0.03] hover:border-amber-500/25 hover:bg-amber-500/[0.06]'
    : 'border-gray-200 bg-white hover:border-amber-500/20 hover:bg-amber-50/30 shadow-sm hover:shadow-lg';
  const cardTitle = darkMode ? 'text-white' : 'text-gray-900';
  const cardDesc = darkMode ? 'text-white/55' : 'text-gray-500';
  const dividerColor = darkMode ? 'border-amber-500/10' : 'border-gray-200';
  const navSignIn = darkMode ? 'text-white/70 hover:text-white' : 'text-gray-600 hover:text-gray-900';
  const ctaSecondary = darkMode
    ? 'border-amber-500/20 text-white/80 hover:border-amber-500/40 hover:bg-amber-500/10 hover:text-white'
    : 'border-gray-300 text-gray-700 hover:border-amber-500/30 hover:bg-amber-50 hover:text-amber-700';
  const statValue = darkMode ? 'text-white' : 'text-gray-900';
  const dotGridColor = darkMode ? 'rgba(245,158,11,0.04)' : 'rgba(245,158,11,0.06)';

  return (
    <div className={`relative min-h-screen overflow-hidden transition-colors duration-500 ${bg}`}>
      {/* ── Background layers ── */}
      <div className={`absolute inset-0 transition-colors duration-500 ${gradientOverlay}`} />

      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(${dotGridColor} 1px, transparent 1px)`,
          backgroundSize: '32px 32px',
        }}
      />

      <div
        className="absolute left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        style={{
          width: 900, height: 900,
          background: darkMode
            ? 'radial-gradient(circle, rgba(245,158,11,0.08) 0%, transparent 55%)'
            : 'radial-gradient(circle, rgba(245,158,11,0.08) 0%, transparent 50%)',
        }}
      />

      {/* ── Flowing consciousness waves ── */}
      <WaveRiver darkMode={darkMode} />

      {/* ── Sticky Navigation ── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrollProgress > 0.05
          ? darkMode
            ? 'bg-[#0f0f14]/80 backdrop-blur-lg border-b border-amber-500/10 shadow-lg shadow-black/20'
            : 'bg-white/80 backdrop-blur-lg border-b border-gray-200 shadow-lg shadow-gray-200/50'
          : ''
      }`}>
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex items-center justify-between py-4 md:py-5">
            <div className={fade('logo')}>
              <OperisLogo size="md" light={darkMode} />
            </div>

            <div className={`flex items-center gap-3 ${fade('logo')}`}>
              <button
                onClick={toggleTheme}
                className={`rounded-lg p-2 transition-all duration-300 ${
                  darkMode
                    ? 'text-white/60 hover:text-white hover:bg-white/10'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                }`}
                aria-label={t('landing.toggleThemeAria')}
              >
                {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>

              <LanguageToggle className="rounded-lg" />

              <Link
                to="/login"
                className={`rounded-lg px-5 py-2 text-sm font-semibold transition-colors duration-300 ${navSignIn}`}
              >
                {t('landing.signIn')}
              </Link>
              <Link
                to="/register"
                className={`rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 ${
                  darkMode
                    ? 'bg-gradient-to-r from-amber-500 to-orange-600 shadow-amber-500/25 hover:shadow-amber-500/40'
                    : 'bg-gradient-to-r from-amber-500 to-amber-600 shadow-amber-500/20 hover:shadow-amber-500/30'
                }`}
              >
                {t('landing.getStarted')}
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* ── Content ── */}
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 pt-20">
        {/* ── Hero ── */}
        <section className="grid items-center gap-10 py-14 md:py-20 lg:grid-cols-2 lg:gap-16 lg:py-24">
          <div className="max-w-xl">
            {/* <div className={`mb-8 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium ${
              darkMode
                ? 'border-indigo-500/20 bg-indigo-500/10 text-indigo-300'
                : 'border-operis-blue/20 bg-operis-blue/5 text-operis-blue'
            } ${fade('badge')}`}>
              <Zap className="h-3.5 w-3.5" />
              Operis Time Platform
            </div> */}

            <h1 className={`text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl md:text-6xl lg:text-[3.6rem] ${headingColor} ${fade('heading')}`}>
              {t('landing.heroLine1')}{' '}
              <span className={`bg-clip-text text-transparent ${
                darkMode
                  ? 'bg-gradient-to-r from-amber-400 via-orange-500 to-amber-500'
                  : 'bg-gradient-to-r from-amber-500 via-orange-500 to-amber-400'
              }`}>
                {t('landing.heroLine2')}
              </span>
            </h1>

            <p className={`mt-6 max-w-lg text-base leading-relaxed sm:text-lg ${bodyColor} ${fade('subtext')}`}>
              {t('landing.heroSubtitle')}
            </p>

            <div className={`mt-10 flex flex-wrap gap-4 ${fade('ctas')}`}>
              <Link
                to="/login"
                className={`group inline-flex items-center gap-2 rounded-xl px-8 py-3.5 font-semibold text-white shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 ${
                  darkMode
                    ? 'bg-gradient-to-r from-amber-500 to-orange-600 shadow-amber-500/30 hover:shadow-amber-500/45'
                    : 'bg-gradient-to-r from-amber-500 to-amber-600 shadow-amber-500/25 hover:shadow-amber-500/35'
                }`}
              >
                {t('landing.getStarted')}
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/register"
                className={`inline-flex items-center gap-2 rounded-xl border px-8 py-3.5 font-semibold transition-all duration-300 ${ctaSecondary}`}
              >
                {t('landing.createAccount')}
              </Link>
            </div>

            <div className={`mt-14 border-t pt-8 ${dividerColor} ${fade('pillars')}`}>
              <p className={`mb-4 text-xs font-semibold uppercase tracking-widest ${mutedColor}`}>
                {t('landing.whyTeamsTitle')}
              </p>
              <ul className="grid gap-5 sm:grid-cols-2 lg:gap-6">
                {trustPillars.map(({ Icon, title, desc }) => (
                  <li key={title} className="flex gap-3">
                    <div
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                        darkMode
                          ? 'bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/25'
                          : 'bg-amber-500/10 text-amber-700 ring-1 ring-amber-500/20'
                      }`}
                    >
                      <Icon className="h-5 w-5" aria-hidden />
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${statValue}`}>{title}</p>
                      <p className={`mt-0.5 text-xs leading-relaxed sm:text-sm ${mutedColor}`}>{desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right: Clock + Waves + Rings + TimeRain */}
          <div className={`flex justify-center lg:justify-end ${fade('clock')}`}>
            <div className="relative flex items-center justify-center overflow-hidden" style={{ minHeight: 520, minWidth: 420 }}>

              {scrollRings.map((ring, i) => {
                const isVisible = scrollProgress >= ring.threshold;
                const isReverse = ring.spin < 0;
                const duration = Math.abs(ring.spin);
                const ringColor = darkMode ? ring.color : ring.colorLight;
                const ringOpacity = darkMode ? ring.opacity : ring.opacityLight;
                return (
                  <div
                    key={i}
                    className="absolute pointer-events-none"
                    style={{
                      width: ring.diameter,
                      height: ring.diameter,
                      top: '50%',
                      left: '50%',
                      marginTop: -ring.diameter / 2,
                      marginLeft: -ring.diameter / 2,
                      opacity: isVisible ? ringOpacity : 0,
                      transition: 'opacity 1.2s cubic-bezier(0.4,0,0.2,1), transform 1.2s cubic-bezier(0.4,0,0.2,1)',
                      transform: `scale(${isVisible ? 1 : 0.6})`,
                    }}
                  >
                    <div
                      className="w-full h-full rounded-full"
                      style={{
                        border: `1px ${ring.dashed ? 'dashed' : 'solid'} ${ringColor}`,
                        animation: isVisible
                          ? `spin ${duration}s linear infinite ${isReverse ? 'reverse' : ''}`
                          : 'none',
                      }}
                    />
                  </div>
                );
              })}

              <AnimatedClock
                size={320}
                glowColor={darkMode ? '#d97706' : '#d97706'}
                lightDial={!darkMode}
              />
            </div>
          </div>
        </section>

        {/* ── Features ── */}
        <section ref={featuresRef} className={`border-t py-20 lg:py-28 ${dividerColor}`}>
          <div className={`mb-14 text-center transition-all duration-700 ${featuresVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <h2 className={`text-3xl font-bold md:text-4xl ${headingColor}`}>
              {t('landing.featuresTitle')}
            </h2>
            <p className={`mx-auto mt-3 max-w-lg ${bodyColor}`}>
              {t('landing.featuresSubtitle')}
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {features.map((f, idx) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  className={`group relative rounded-2xl border p-7 transition-all duration-500 hover:-translate-y-1 ${cardBg} ${
                    featuresVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${idx * 120}ms` }}
                >
                  <div className={`mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${f.gradient} shadow-lg transition-transform duration-500 group-hover:scale-110`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className={`text-lg font-semibold ${cardTitle}`}>{f.title}</h3>
                  <p className={`mt-2 text-sm leading-relaxed ${cardDesc}`}>{f.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── Capabilities ── */}
        <section ref={capsRef} className={`border-t py-20 lg:py-28 ${dividerColor}`}>
          <div className={`mb-14 text-center transition-all duration-700 ${capsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <h2 className={`text-3xl font-bold md:text-4xl ${headingColor}`}>
              {t('landing.capabilitiesTitle')}
            </h2>
            <p className={`mx-auto mt-3 max-w-lg ${bodyColor}`}>
              {t('landing.capabilitiesSubtitle')}
            </p>
          </div>

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {capabilities.map((c, idx) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.title}
                  className={`flex flex-col items-center text-center transition-all duration-700 ${
                    capsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${idx * 100}ms` }}
                >
                  <div className={`mb-4 flex h-14 w-14 items-center justify-center rounded-2xl transition-colors duration-300 ${
                    darkMode
                      ? 'bg-amber-500/10 ring-1 ring-amber-500/20'
                      : 'bg-amber-500/10 ring-1 ring-amber-500/20'
                  }`}>
                    <Icon className={`h-6 w-6 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                  </div>
                  <h3 className={`text-base font-semibold ${cardTitle}`}>{c.title}</h3>
                  <p className={`mt-1.5 text-sm ${cardDesc}`}>{c.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── Footer ── */}
        <footer className={`border-t py-10 ${dividerColor}`}>
          <div className={`flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 text-sm ${mutedColor}`}>
            <span>
              &copy; {new Date().getFullYear()} {t('layout.footer.copyrightPrefix')} · {t('layout.footer.tagline')}
            </span>
            <span className="hidden sm:inline opacity-40" aria-hidden>
              |
            </span>
            <a
              href="http://cms.trilogy.fanselector.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md px-2 py-1 hover:text-gray-600 dark:hover:text-white/70 transition-colors"
            >
              <img
                src="/gblogo.png"
                alt="Greybeard"
                className="h-5 sm:h-6 w-auto opacity-95 hover:opacity-100 transition-opacity"
              />
              <span className="text-xs sm:text-sm">
                <span className="opacity-75">{t('layout.footer.technologyPartner')} ·</span> Greybeard
              </span>
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default LandingPage;
